import React, { useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const { scrollY } = useScroll();
  const bg = useTransform(scrollY, [0, 80], ['rgba(5,10,8,0)', 'rgba(5,10,8,0.75)']);
  const border = useTransform(scrollY, [0, 80], ['rgba(255,255,255,0)', 'rgba(16,185,129,0.15)']);
  const navigate = useNavigate();
  const location = useLocation();
  const onLanding = location.pathname === '/';

  const navItems = [
    { label: 'Services', href: '#services', type: 'anchor' },
    { label: 'Why Us', href: '#why-us', type: 'anchor' },
    { label: 'Work', href: '#portfolio', type: 'anchor' },
    { label: 'Pricing', href: '#pricing', type: 'anchor' },
    { label: 'Blog', to: '/blog', type: 'route' },
    { label: 'FAQ', href: '#faq', type: 'anchor' },
  ];

  const goHomeAndScroll = (href) => {
    navigate('/');
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const scrollToSection = (href) => {
    if (!onLanding) {
      goHomeAndScroll(href);
      setIsOpen(false);
      return;
    }
    const el = document.querySelector(href);
    if (el) { el.scrollIntoView({ behavior: 'smooth' }); setIsOpen(false); }
  };

  return (
    <motion.header
      style={{ backgroundColor: bg, borderColor: border }}
      className="fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-24">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/" onClick={() => onLanding && window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center" data-testid="header-logo-btn">
              <img src="/assets/oh-logo.png" alt="OutsourcedHub" className="h-16 w-auto" />
            </Link>
          </motion.div>

          <nav className="hidden lg:flex items-center gap-9">
            {navItems.map((item, index) => {
              const testId = `nav-${item.label.toLowerCase().replace(' ', '-')}`;
              const cls = "text-sm text-slate-300 hover:text-white transition-colors duration-300 relative group font-medium";
              const underline = <span className="absolute -bottom-1.5 left-0 w-0 h-0.5 bg-emerald-400 group-hover:w-full transition-all duration-300" />;
              if (item.type === 'route') {
                return (
                  <motion.div key={item.label} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.08 }}>
                    <Link to={item.to} className={cls} data-testid={testId}>{item.label}{underline}</Link>
                  </motion.div>
                );
              }
              return (
                <motion.button
                  key={item.label}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  onClick={() => scrollToSection(item.href)}
                  className={cls}
                  data-testid={testId}
                >
                  {item.label}{underline}
                </motion.button>
              );
            })}
          </nav>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="hidden lg:block"
          >
            <Button
              onClick={() => scrollToSection('#contact')}
              data-testid="header-cta"
              className="bg-emerald-500 hover:bg-emerald-400 text-[#04120C] px-6 py-2.5 rounded-full font-bold transition-all duration-300 hover:shadow-[0_0_30px_rgba(16,185,129,0.5)]"
            >
              Get Started
            </Button>
          </motion.div>

          <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden text-white p-2" data-testid="mobile-menu-toggle">
            {isOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0 }}
        className="lg:hidden overflow-hidden bg-[#050A08]/95 backdrop-blur-xl border-t border-emerald-500/10"
      >
        <div className="px-6 py-6 space-y-3">
          {navItems.map((item) => {
            if (item.type === 'route') {
              return (
                <Link key={item.label} to={item.to} onClick={() => setIsOpen(false)} className="block w-full text-left text-slate-300 hover:text-white py-2 font-medium">
                  {item.label}
                </Link>
              );
            }
            return (
              <button key={item.label} onClick={() => scrollToSection(item.href)} className="block w-full text-left text-slate-300 hover:text-white py-2 font-medium">
                {item.label}
              </button>
            );
          })}
          <Button onClick={() => scrollToSection('#contact')} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#04120C] px-6 py-3 rounded-full font-bold">
            Get Started
          </Button>
        </div>
      </motion.div>
    </motion.header>
  );
}
